import { useCallback, useEffect, useRef, useState } from "react";

const INITIAL_PAGINATION = (perPage) => ({
  page: 1,
  perPage,
  total: 0,
  lastPage: 1,
  hasNextPage: false,
  hasPreviousPage: false,
});

/**
 * Shared server-pagination state. fetchPage must return { items, pagination }.
 * resetKey should change whenever server-side filters change.
 */
function usePaginatedList({ fetchPage, resetKey = "", perPage = 25, enabled = true }) {
  const [page, setPage] = useState(1);
  const [state, setState] = useState({
    items: [],
    pagination: INITIAL_PAGINATION(perPage),
    isLoading: enabled,
    error: null,
  });
  const requestId = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [resetKey]);

  const [refreshToken, setRefreshToken] = useState(0);

  const reload = useCallback(() => {
    setRefreshToken((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    const currentRequest = ++requestId.current;
    setState((current) => ({ ...current, isLoading: true, error: null }));

    Promise.resolve(fetchPage({ page, perPage }))
      .then((result) => {
        if (currentRequest !== requestId.current) return;
        const nextPage = result.pagination?.page || page;
        setState((current) => ({
          ...current,
          items: result.items,
          pagination: result.pagination,
          isLoading: false,
        }));
        if (nextPage !== page) setPage(nextPage);
      })
      .catch((error) => {
        if (currentRequest !== requestId.current) return;
        setState((current) => ({ ...current, items: [], isLoading: false, error }));
      });

    return () => {
      requestId.current += 1;
    };
  }, [enabled, fetchPage, page, perPage, refreshToken]);

  return { ...state, page, setPage, reload };
}

export default usePaginatedList;
